import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { FETCH_PRODUCT_DATA_API } from "../../Api";
import { Button, Input } from "@material-tailwind/react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import airbg from "../../../assets/airbg.jpg";
import waterbg from "../../../assets/waterbg.jpg";
import pharma from "../../../assets/pharma.jpg";
import hygiene from "../../../assets/hygiene.jpg";
import enviroLoader from "../../../assets/enviroLoader.gif";

const ProductPage = () => {
    const [productData, setProductData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(null);
    const [email, setEmail] = useState("");
    // eslint-disable-next-line
    const [searchQuery, setSearchQuery] = useState("");

    const navigate = useNavigate();

   useEffect(() => {
    const currentPath = window.location.pathname;
    const segments = currentPath.split("/");
    const productsIndex = segments.findIndex(
        (segment) => segment.toLowerCase() === "products"
    );

    if (productsIndex !== -1 && segments.length > productsIndex + 1) {
        const categoryId = decodeURIComponent(segments[productsIndex + 1]);
        const subCategoryId = segments[productsIndex + 2]
            ? decodeURIComponent(segments[productsIndex + 2])
            : null;

        const apiURL = `${FETCH_PRODUCT_DATA_API}${categoryId}`;

        fetch(apiURL)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch product data");
                return res.json();
            })
            .then((data) => {
                // ✅ Sort SubCategories by uId (ascending)
                if (data?.subcollections?.SubCategories?.length > 0) {
                    data.subcollections.SubCategories.sort(
                        (a, b) => (a.uId || 0) - (b.uId || 0)
                    );
                }

                setProductData(data);
                console.log("Fetched Product Data (sorted):", data);

                if (data?.subcollections?.SubCategories?.length > 0) {
                    if (subCategoryId) {
                        // case: user landed with subCategory in URL
                        setActiveTab(subCategoryId);
                    } else {
                        // case: no subCategory in URL → use first one
                        const firstSub = data.subcollections.SubCategories[0];
                        setActiveTab(firstSub.id || firstSub.name);

                        // Update URL with first subCategory
                        const newPath = `/products/${categoryId}/${firstSub.id || firstSub.name}`;
                        window.history.replaceState({}, "", newPath);
                    }
                }
                setLoading(false);
            })
            .catch((err) => {
                toast.error("Error fetching product data. Please try again later.");
                setError(err.message);
                setLoading(false);
            });
    } else {
        toast.error("Invalid product path.");
        setError("Invalid product path.");
        setLoading(false);
    }
}, []);



    // Get subcategories safely
    const subCategories = (productData?.subcollections?.SubCategories || []).sort(
  (a, b) => a.uId - b.uId
);

    const activeSubCategory = subCategories.find(sub => sub.id === activeTab) || {};

    const allProducts = activeSubCategory?.subcollections
        ? Object.values(activeSubCategory.subcollections)?.[0] || []
        : [];

    // Filter products based on the search query
    const products = allProducts.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    console.log("Active SubCategory Products:", allProducts);

    // Background Image Logic
    const getBackgroundImage = () => {
        switch (productData?.name) {
            case "Air Quality":
                return airbg;
            case "Water Quality":
                return waterbg;
            case "Pharma and Bio-Tech":
                return pharma;
            default:
                return hygiene;
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubscribe = () => {
        if (!email.trim()) {
            toast.error("Please enter your email.");
            return;
        }

        if (!validateEmail(email)) {
            toast.error("Invalid email format. Please enter a valid email.");
            return;
        }

        // Clear email field & show success toast
        setEmail("");
        toast.success("Thanks for subscribing! We'll notify you once we have products.");
    };

    const productRef = useRef(null);

    const handleScrollToPromo = () => {
        if (productRef.current) {
            productRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };


    const handleProductClick = (selectedProduct) => {
        if (!selectedProduct || !selectedProduct.name) return;

        // Store details of the selected product
        const productDetails = {
            name: selectedProduct.name,
            img: selectedProduct.img[0], // Store the first image
            desc: selectedProduct.desc,
            features: selectedProduct.feat || [],
        };

        // Get all products except the selected one
        const allOtherProducts = subCategories
            .flatMap(sub => Object.values(sub.subcollections || {})?.[0] || [])
            .filter(product => product.name !== selectedProduct.name); // Exclude the selected product

        // Store data in session storage
        sessionStorage.setItem("selectedProduct", JSON.stringify(productDetails));
        sessionStorage.setItem("otherProducts", JSON.stringify(allOtherProducts));

        // Navigate to the about product page
        const categoryId = encodeURIComponent(productData.id);
        const subCategoryId = encodeURIComponent(activeTab);
        const productId = encodeURIComponent(selectedProduct.id);

        navigate(`/products/${categoryId}/${subCategoryId}/${productId}`);
    };


    return (
        <div>
            {loading ? (
                <div className="flex items-center justify-center h-screen">
                    <img
                        src={enviroLoader}
                        alt="Loading..."
                        className="w-40 h-40"
                    />
                </div>
            ) : error ? (
                <div className="flex items-center justify-center h-screen text-red-500 text-xl">
                    {error}
                </div>
            ) : (
                <>
                    {/* Hero Section */}
                    <div
                        className="w-full bg-cover bg-center bg-no-repeat relative overflow-hidden absolute"
                        style={{ backgroundImage: `url(${getBackgroundImage()})`, height: "500px" }}
                    >
                        <div className="absolute inset-0 bg-black opacity-50"></div>
                        <div className="relative z-10 text-white flex flex-col items-start h-full px-6 md:px-12 space-y-6 mt-40">
                            <h2 className="text-2xl md:text-6xl leading-tight">
                                Get Set for {["A", "E", "I", "O", "U"].includes(productData?.name?.charAt(0).toUpperCase()) ? "an" : "a"} <br className="hidden sm:block" /> {productData?.name} Revolution
                            </h2>
                            {/* Explore Products Button */}
                            <button
                                href=""
                                onClick={handleScrollToPromo}
                                className="flex items-center justify-between bg-white text-black text-lg font-medium py-3 px-6 rounded-full shadow-md transition-all duration-300 border border-gray-300"
                                style={{ width: "fit-content" }}
                            >
                                <span className="mr-3">Explore Products</span>
                                <span className="flex items-center justify-center w-8 h-8 bg-green-400 rounded-full text-black">
                                    ➜
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Product Section */}
                    <div className="mx-auto py-20 px-6 md:px-12" ref={productRef}>
                        <h2 className="text-3xl md:text-6xl font-bold mb-8">
                            {productData?.name} Products
                        </h2>

                        <div className="flex flex-col md:flex-row gap-8">
                            {/* Left Section - Subcategories */}
                            <div className="md:w-1/4 border-r border-gray-300 pr-4">
                                <h3 className="text-xl font-semibold mb-4">Categories</h3>
                                <div className="flex flex-col gap-3">
                                    {subCategories.map((sub) => {
                                        // Dynamically find the products array for this subcategory
                                        const subKey = `${sub.id}_Products`;
                                        const count =
                                            productData?.subcollections?.SubCategories
                                                ?.find((s) => s.id === sub.id)
                                                ?.subcollections?.[subKey]?.length || 0;

                                        return (
                                            <button
                                                key={sub.id}
                                                onClick={() => {
                                                    setActiveTab(sub.id);
                                                    navigate(`/products/${productData?.id}/${sub.id}`);
                                                }}
                                                className={`flex justify-between items-center px-4 py-2 text-left border rounded-lg transition-colors focus:outline-none ${activeTab === sub.id
                                                        ? "bg-green-500 text-white border-green-500"
                                                        : "bg-transparent text-gray-700 border-gray-300 hover:bg-green-100 hover:text-green-700"
                                                    }`}
                                            >
                                                <span>{sub.name}</span>
                                                <span className="font-semibold">({count})</span>
                                            </button>
                                        );
                                    })}

                                </div>
                            </div>

                            {/* Right Section - Products */}
                            <div className="md:w-3/4 pl-4">
                                {products.length === 0 ? (
                                    <div className="w-full flex justify-center">
                                        <div className="flex flex-col items-start text-left space-y-4 p-4 max-w-[800px] w-full">
                                            <FaBell className="text-yellow-500 text-5xl" />
                                            <p className="text-lg text-gray-600 font-medium leading-snug">
                                                We’re sorry to inform you that there are no products available at the moment.
                                                <br />
                                                Get notified when we have new products.
                                            </p>
                                            <div className="flex flex-row items-center space-x-2 w-full">
                                                <Input
                                                    type="email"
                                                    label="Enter your email"
                                                    className="border border-black rounded-md px-4 py-2 text-black focus:outline-none flex-1 h-12"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                                <Button
                                                    className="bg-green-500 text-white rounded-md px-4 hover:bg-green-600 transition-colors h-10 whitespace-nowrap"
                                                    onClick={handleSubscribe}
                                                >
                                                    NOTIFY ME
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {products.map((product, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => handleProductClick(product)}
                                                className="flex flex-col justify-between rounded-2xl overflow-hidden border-2 border-gray-300 p-4 cursor-pointer"
                                            >
                                                <h3 className="text-gray-900 font-extrabold text-xl">{product.name}</h3>
                                                <p className="text-gray-600 text-sm mt-2 leading-relaxed overflow-hidden text-ellipsis line-clamp-3">
                                                    {product.desc}
                                                </p>
                                                <div className="mt-4 rounded-2xl overflow-hidden">
                                                    <img
                                                        src={product.img[0]}
                                                        alt={product.name}
                                                        className="w-full h-48 object-cover"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </>
            )}
            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
};

export default ProductPage;
